import { useTranslations } from "@fenetech/translations";
import { Divider, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import CheckBoxForm from "components/Common/CheckBoxForm";
import EditPreviewTextFieldForm from "components/Common/EditPreviewTextFieldForm";
import SelectForm from "components/Common/SelectForm";
import TextFieldForm from "components/Common/TextFieldForm";
import { ESignatureCaptureMethodEnum } from "helpers/enums";
import { IESignatureReportSetting } from "models/IESignature";
import React from 'react';

interface IProps {
    reports: IESignatureReportSetting[],
    eSignatureCaptureMethod: ESignatureCaptureMethodEnum,
    setESignatureCaptureMethod: React.Dispatch<React.SetStateAction<ESignatureCaptureMethodEnum>>
    formatBodyPreviewText: (templateText: string) => string
    disabled: boolean,
}

const ESignatureDialogForm: React.FC<IProps> = ({ reports, eSignatureCaptureMethod, setESignatureCaptureMethod, disabled, formatBodyPreviewText }: IProps) => {

    const tm = useTranslations();

    return <>
        <FormControl fullWidth sx={{mt: 2}}>
            <InputLabel id="captureMethod">{tm.Get("Type")}</InputLabel>
            <Select
                labelId="captureMethod"
                id="captureMethod"
                value={eSignatureCaptureMethod}
                label={tm.Get("Type")}
                disabled={disabled}
                onChange={(e) => {
                    setESignatureCaptureMethod(e.target.value as ESignatureCaptureMethodEnum);
                }}>
                <MenuItem value={ESignatureCaptureMethodEnum.Email}>{tm.Get("Email")}</MenuItem>
                <MenuItem value={ESignatureCaptureMethodEnum.Embedded}>{tm.Get("Embedded Browser")}</MenuItem>
            </Select>
        </FormControl>

        <SelectForm
            fieldName="reportID"
            dataSource={reports}
            translateDisplayField={false}
            keyField="reportID"
            valueField="reportID"
            displayField="reportName"
            label={tm.Get("Document")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Document")) }}
            readOnly={false}
            disabled={disabled}
        />

        <Divider>{tm.Get("Signer")}</Divider>

        <TextFieldForm
            fieldName="signerName"
            variant="standard"
            readOnly={false}
            label={tm.Get("Name")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Name")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Name")),
            }}
            fullWidth
            disabled={disabled}
        />

        <TextFieldForm
            fieldName="signerEmail"
            variant="standard"
            readOnly={false}
            label={tm.Get("Email")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Email")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Email")),
            }}
            fullWidth
            disabled={disabled}
        />

        <Divider>{tm.Get("Sender")}</Divider>

        <TextFieldForm
            fieldName="senderName"
            variant="standard"
            readOnly={false}
            label={tm.Get("Name")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Name")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Name")),
            }}
            fullWidth
            disabled={disabled}
        />

        <TextFieldForm
            fieldName="senderEmail"
            variant="standard"
            readOnly={false}
            label={tm.Get("Email")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Email")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Email")),
            }}
            fullWidth
            disabled={disabled}
        />

        <CheckBoxForm
            fieldName="ccSender"
            label={tm.Get("Receive Carbon Copy")}
            readOnly={false}
            sx={{paddingLeft: 0}}
            disabled={disabled}
        />

        <Divider>{tm.Get("Message")}</Divider>

        <TextFieldForm
            fieldName="emailSubject"
            variant="standard"
            readOnly={false}
            label={tm.Get("Subject")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Subject")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Subject")),
            }}
            fullWidth
            disabled={disabled}
        />

        <EditPreviewTextFieldForm
            fieldName="emailBody"
            fieldsInvolvedInTemplate={["signerName", "senderName"]}
            formatPreviewText={formatBodyPreviewText}
            readOnly={false}
            previewLabel={tm.Get("Body")}
            editLabel={tm.Get("Template")}
            requiredRule={{ value: true, message: tm.GetWithParams("{0} Required", tm.Get("Body")) }}
            validateValue={{
                isNotEmpty: (value: string) => new RegExp("^(?!\\s*$).+").test(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get("Body")),
            }}
            disabled={disabled}
        />
    </>;
}

export default ESignatureDialogForm;